'use client';

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import CrossMark from 'components/Icons/CrossMark';
import { useRouter } from 'next/router';
import Caret from 'components/Icons/Caret';
import { imageLoader } from 'functions/imageLoader';

interface GalleryItem {
  src: string;
  alt: string;
  video: string;
}
const reelsList = ["DIsY-HSyzPx", "DIviqQMRX5V", "Cwri2o2N1t_", "CuGBuXjAmfq", "C6AK4ZXCjLf", "CxCvxHotptI","CvCBOqqRUBL","CpqrQ6zoABN", "C3CASBRyDRW","Cr5KF5EgFkt","CrnG8pdIOOf","CqM4Oh5MSW5","CmgBD3MOvxV","C2dolT3SBrn" ];
/*

https://www.instagram.com/reel/C3CASBRyDRW/
https://www.instagram.com/reel/CpqrQ6zoABN/
*/
const InstagramIcon: React.FC<IconProps> = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
    </svg>
  );

interface IconProps {
  className?: string;
}

const PlayIcon: React.FC<IconProps> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="5 3 19 12 5 21 5 3"></polygon>
  </svg>
);

/*
const CaretIcon: React.FC<IconProps> = ({ className }) => (
    <svg className={className} viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="4.5 9 7.5 6 4.5 3"></polyline>
    </svg>
  );*/
interface VideoPopupProps {
  videoSrc: string;
  onClose: () => void;
}
const VideoPopup: React.FC<VideoPopupProps> = ({ videoSrc, onClose }) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    // Autoplay the video when the component mounts
    if (videoRef.current) {
      videoRef.current.play().catch(error => console.log("Autoplay was prevented:", error));
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div ref={popupRef} className="relative w-full max-w-md h-[80vh] flex flex-col">
        <button
          onClick={onClose}
          className="absolute -top-10 lg:-top-12 right-0 text-white hover:text-gray-300 transition-colors bg-red-600 hover:bg-red-700 rounded-full p-2 z-10"
        >
            <CrossMark width={18} height={18} fill="currentColor" />
        </button>
        <div className="relative w-full h-full">
          <video 
            ref={videoRef}
            controls 
            autoPlay
            playsInline
            loop
            className="absolute top-0 left-0 w-full h-full object-contain"
          >
            <source src={videoSrc} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    </div>,
    document.body
  );
};

const CommunityShowcase: React.FC = () => {
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState(false);
  const showcaseRef = useRef<HTMLDivElement>(null);

  const router = useRouter();

  const instagramAddress = useMemo(() => {
    return router.locale === 'ca' ? 'https://www.instagram.com/dresdenvisionca' : 'https://www.instagram.com/dresdenvision';
  }, [router.locale]);

  useEffect(() => {
    setIsMounted(true);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (showcaseRef.current) {
      observer.observe(showcaseRef.current);
    }

    return () => {
      setIsMounted(false);
      observer.disconnect();
    };
  }, []);

    const galleryItems: GalleryItem[] = useMemo(() => {
        const newGalleryItems: GalleryItem[] = [];
        //randomize reelsList
            reelsList.sort(() => Math.random() - 0.5);
            reelsList.forEach((igReelID, index) => {
                newGalleryItems.push({ src: `https://w.dresden.vision/img/community/ig_${igReelID}.jpg`, alt: 'Image '+index, video: `https://cdn.dresden.vision/video/community/ig_${igReelID}.mp4` });
            });
            return newGalleryItems;
        }   , []);



  const openVideo = (videoSrc: string) => setSelectedVideo(videoSrc);
  const closeVideo = () => setSelectedVideo(null);

  if (!isVisible) {
    return <div ref={showcaseRef} className="h-96 bg-gray-100"></div>;
  }

  return (
    <section ref={showcaseRef} className="bg-white py-[16px] px-4 sm:py-[24px]">
    <div className="mx-auto w-full max-w-[1224px]">
     
      <h1 className="text-3xl font-bold text-center my-2">Welcome to our community</h1>
      <p className="text-center mb-8">Good glasses for smart people</p>
      <div className="relative">
      <Swiper
        modules={[Navigation, Pagination]}
        spaceBetween={4}
        slidesPerView={3}
        navigation={{
          nextEl: '[data-reviews-slider-next]',
              prevEl: '[data-reviews-slider-previous]'
        }}
        pagination={{ clickable: true }}
        breakpoints={{
          768: {
            slidesPerView: 4,
          },
          1024: {
            slidesPerView: 7,
          },
        }}
        className="relative"
      >
        {galleryItems && galleryItems.map((item, index) => (
         <SwiperSlide key={index}>
         <div className="relative group cursor-pointer" onClick={() => openVideo(item.video)}>
           <Image 
             src={item.src} 
             alt={item.alt}
             width={400}
             height={400}
             className="w-full h-auto"
             loader={imageLoader}
           />
           <div className="absolute inset-0 flex items-center justify-center lg:hidden">
             <PlayIcon className="w-12 h-12 text-white opacity-70 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity duration-300" />
           </div>
           <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity duration-300 hidden lg:flex items-center justify-center">
             <PlayIcon className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
           </div>
         </div>
       </SwiperSlide>
        ))}
        
      </Swiper>
      <div
            data-reviews-slider-previous
            className="transition-color absolute left-[-68px] top-[70px] z-10 hidden h-[42px] w-[42px] rotate-90 transform cursor-pointer items-center justify-center rounded-full bg-white text-brand-black100 shadow-card duration-500 hover:text-brand-orange lg:flex">
            <Caret className="mt-[3px]" fill="currentColor" />
          </div>
          <div
            data-reviews-slider-next
            className="rotate absolute right-[-68px] top-[70px] hidden h-[42px] w-[42px]  -rotate-90 transform cursor-pointer items-center justify-center rounded-full bg-white text-brand-black100 shadow-card duration-500 hover:text-brand-orange lg:flex">
            <Caret className="mt-[3px]" fill="currentColor" />
          </div>
        </div>
      {isMounted && selectedVideo && (
        <VideoPopup videoSrc={selectedVideo} onClose={closeVideo} />
      )}

<div className="text-center mt-8">
  <a 
    href={instagramAddress}
    className="inline-flex items-center text-[#00AEC7]"
    target="_blank"
    rel="noopener noreferrer nofollow"
  >
    <InstagramIcon className="w-5 h-5 mr-2 flex-shrink-0" />
    <span className="text-sm font-medium leading-5">Follow us on Instagram</span>
  </a>
</div>
    </div>
    </section>
  );
};

export default CommunityShowcase;